/**
 * SCOREBOARD-01 — the shop's numbers, automatic.
 *
 * ScoreboardPane (Team tab): period toggle, KPI tiles, the tech leaderboard
 * (calls · PLUS plans · shout-outs · invoiced $), and a one-click review
 * shout-out logger. Derived live from the records — nothing hand-kept.
 *
 * TvScoreboard (/team/tv): the break-room mode. Chromeless, dark, huge type,
 * auto-rotating slides, refreshes itself every minute. Cast the tab to the
 * TV from Chrome (⋮ → Cast) or AirPlay the window — no extra hardware logic
 * needed; the page just stays live.
 *
 * Design notes (dataviz method): stat tiles are typographic (no chart);
 * leaderboard magnitude reads through ONE brand hue as thin 4px-rounded bars
 * anchored left, numbers in ink tokens beside them — no multi-hue series, no
 * color-by-rank.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Trophy, Wrench, ShieldCheck, Star, DollarSign, Tv, Plus, X, Loader2, Expand,
} from "lucide-react";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { useAllActiveTechs } from "@/features/board/api";
import { TechAvatar } from "@/components/TechAvatar";
import { useScoreboard, useAddShoutout, useDeleteShoutout, useServiceProductivity, useInstallLeaders, type Period, type TechRow } from "./scoreboard";

const money0 = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const fmtDay = (s: string | null) => (s ? new Date(s + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—");

// ── the office pane ─────────────────────────────────────────────────────────
export function ScoreboardPane() {
  const [period, setPeriod] = useState<Period>("week");
  const { data } = useScoreboard(period);
  const [addOpen, setAddOpen] = useState(false);

  const top = (data?.rows ?? []).filter((r) => r.calls + r.plans + r.shoutouts > 0 || r.invoiced > 0);
  const maxCalls = Math.max(1, ...top.map((r) => r.calls));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {(["week", "month"] as const).map((p) => (
          <button key={p} type="button" onClick={() => setPeriod(p)}
            className={cn(
              "rounded-full px-3 py-1.5 text-[13px] font-semibold transition-colors",
              period === p ? "bg-brand-700 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
            )}>
            {p === "week" ? "This week" : "This month"}
          </button>
        ))}
        <span className="text-xs text-slate-400">since {fmtDay(data?.start ?? null)}</span>
        <span className="ml-auto flex items-center gap-2">
          <button type="button" onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1.5 text-[13px] font-semibold text-amber-800 transition-colors hover:bg-amber-100">
            <Star className="h-3.5 w-3.5" /> Log a review shout-out
          </button>
          <a href="/team/tv" target="_blank" rel="noreferrer"
            title="Opens the break-room view — cast this tab to the TV from Chrome (⋮ → Cast) or AirPlay"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1.5 text-[13px] font-semibold text-white transition-colors hover:bg-slate-700">
            <Tv className="h-3.5 w-3.5" /> TV mode
          </a>
        </span>
      </div>

      {/* KPI tiles — typographic, no chart. */}
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <Kpi icon={Wrench} label="Calls Completed" value={data ? String(data.totals.calls) : "…"} tone="text-brand-700" />
        <Kpi icon={ShieldCheck} label="PLUS Plans Sold" value={data ? String(data.totals.plans) : "…"} tone="text-emerald-600" />
        <Kpi icon={Star} label="Review Shout-Outs" value={data ? String(data.totals.shoutouts) : "…"} tone="text-amber-600" />
        <Kpi icon={DollarSign} label={`Invoiced${data?.invoicedAsOf ? ` · thru ${fmtDay(data.invoicedAsOf)}` : ""}`}
          value={data ? money0(data.totals.invoiced) : "…"} tone="text-slate-700"
          info={data?.invoicedAsOf ? "From the ESC sales sync, which runs behind. The total fills in as it catches up." : undefined} />
      </div>

      {/* Leaderboard */}
      <Card className="overflow-hidden">
        <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-2">
          <Trophy className="h-4 w-4 text-amber-500" />
          <h3 className="text-[13px] font-semibold text-slate-700">Leaderboard</h3>
          <span className="ml-auto text-[11px] text-slate-400">calls · plans · shout-outs · invoiced</span>
        </div>
        <ul className="divide-y divide-slate-100">
          {top.slice(0, 14).map((r, i) => (
            <LeaderRow key={r.empno} r={r} rank={i + 1} maxCalls={maxCalls} />
          ))}
          {top.length === 0 && <li className="px-4 py-6 text-center text-sm text-slate-400">Nothing recorded for this period yet.</li>}
        </ul>
      </Card>

      {/* TV-SLIDES-01 (Seth 2026-08-21, from the break-room deck): the two
          boards the shop already celebrates — weekly Service Productivity %
          and the install-sales race (count · avg ticket · margin vs the 45%
          goal). Derived live; the PowerPoint ritual retires. */}
      <ServiceProductivityBoard />
      <InstallLeadersBoard />

      {/* Recent shout-outs */}
      {(data?.shoutoutList?.length ?? 0) > 0 && <ShoutoutList />}

      {addOpen && <AddShoutoutModal onClose={() => setAddOpen(false)} />}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone, info }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string; tone: string; info?: ReactNode }) {
  return (
    <div className="rounded-xl border border-hairline bg-white p-3.5 shadow-e1">
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        <Icon className="h-3.5 w-3.5 text-slate-300" /> {label}
        {info && <InfoTip label="Where this number comes from">{info}</InfoTip>}
      </div>
      <div className={cn("mt-1 text-2xl font-bold leading-none tracking-tight tabular-nums", tone)}>{value}</div>
    </div>
  );
}

function LeaderRow({ r, rank, maxCalls, dark }: { r: TechRow; rank: number; maxCalls: number; dark?: boolean }) {
  return (
    <li className={cn("flex items-center gap-3 px-4", dark ? "py-3.5" : "py-2.5")}>
      <span className={cn(
        "flex shrink-0 items-center justify-center rounded-full font-bold tabular-nums",
        dark ? "h-9 w-9 text-lg" : "h-6 w-6 text-xs",
        rank === 1 ? "bg-amber-100 text-amber-800" : rank === 2 ? "bg-slate-200 text-slate-600" : rank === 3 ? "bg-orange-100 text-orange-800" : dark ? "bg-slate-800 text-slate-400" : "bg-slate-50 text-slate-400",
      )}>
        {rank}
      </span>
      <TechAvatar empno={r.empno} name={r.name} size={dark ? "md" : "sm"} />
      <span className="w-40 min-w-0 shrink-0">
        <span className={cn("block truncate font-semibold", dark ? "text-xl text-slate-100" : "text-sm text-slate-800")}>{r.name}</span>
        {r.dept && <span className={cn("block truncate text-[10px] uppercase tracking-wider", dark ? "text-slate-500" : "text-slate-400")}>{r.dept}</span>}
      </span>
      {/* magnitude bar — one hue, thin, 4px rounded, baseline-anchored */}
      <span className="min-w-0 flex-1">
        <span className={cn("block h-2 overflow-hidden rounded", dark ? "bg-slate-800" : "bg-slate-100")}>
          <span className={cn("block h-full rounded", dark ? "bg-brand-500" : "bg-brand-600")} style={{ width: `${Math.round((r.calls / maxCalls) * 100)}%` }} />
        </span>
      </span>
      <span className={cn("w-12 text-right font-bold tabular-nums", dark ? "text-xl text-slate-100" : "text-sm text-slate-800")}>{r.calls}</span>
      <span className={cn("w-12 text-right tabular-nums", dark ? "text-lg text-emerald-400" : "text-sm text-emerald-600")}>{r.plans}</span>
      <span className={cn("w-12 text-right tabular-nums", dark ? "text-lg text-amber-400" : "text-sm text-amber-600")}>{r.shoutouts}</span>
      <span className={cn("hidden w-24 text-right tabular-nums sm:block", dark ? "text-lg text-slate-300" : "text-sm text-slate-500")}>{r.invoiced > 0 ? money0(r.invoiced) : "—"}</span>
    </li>
  );
}

function ShoutoutList() {
  const { data } = useScoreboard("month");
  const { data: techs } = useAllActiveTechs();
  const del = useDeleteShoutout();
  const nameOf = useMemo(() => new Map((techs ?? []).map((t) => [t.empno, (t.firstname ?? "") || (t.empname ?? "") || t.empno])), [techs]);
  const list = data?.shoutoutList ?? [];
  if (!list.length) return null;
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 border-b border-slate-100 bg-amber-50/50 px-4 py-2">
        <Star className="h-4 w-4 text-amber-500" />
        <h3 className="text-[13px] font-semibold text-slate-700">Recent Shout-Outs</h3>
      </div>
      <ul className="divide-y divide-slate-100">
        {list.slice(0, 6).map((s) => (
          <li key={s.id} className="flex items-start gap-3 px-4 py-2.5 text-sm">
            <span className="mt-0.5 shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
              {"★".repeat(Math.max(1, Math.min(5, s.stars ?? 5)))}
            </span>
            <span className="min-w-0 flex-1">
              <span className="font-semibold text-slate-800">{s.empno ? (nameOf.get(s.empno) ?? s.empno) : "Team"}</span>
              {s.reviewer_name && <span className="text-slate-500"> — from {s.reviewer_name}</span>}
              {s.source && <span className="text-slate-400"> · {s.source}</span>}
              {s.quote && <span className="block truncate text-slate-500">&ldquo;{s.quote}&rdquo;</span>}
            </span>
            <button type="button" onClick={() => del.mutate(s.id)} title="Remove"
              className="rounded p-1 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-500">
              <X className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function AddShoutoutModal({ onClose }: { onClose: () => void }) {
  const { data: techs } = useAllActiveTechs();
  const add = useAddShoutout();
  const [empno, setEmpno] = useState("");
  const [reviewer, setReviewer] = useState("");
  const [source, setSource] = useState("Google");
  const [stars, setStars] = useState(5);
  const [quote, setQuote] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="flex items-center gap-2 text-base font-bold text-slate-900">
          Log a Review Shout-Out
          <InfoTip label="What counts as a shout-out">A positive online review that names, or clearly means, one of the crew. It lands on the leaderboard and the break-room TV.</InfoTip>
        </h3>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <select value={empno} onChange={(e) => setEmpno(e.target.value)} className="col-span-2 rounded-md border border-hairline px-2 py-2 text-sm focus:border-brand-500 focus:outline-none">
            <option value="">Who earned it?</option>
            {(techs ?? []).map((t) => <option key={t.empno} value={t.empno}>{(t.firstname ?? "") || t.empname || t.empno}</option>)}
          </select>
          <input value={reviewer} onChange={(e) => setReviewer(e.target.value)} placeholder="Reviewer name" className="rounded-md border border-hairline px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" />
          <select value={source} onChange={(e) => setSource(e.target.value)} className="rounded-md border border-hairline px-2 py-2 text-sm focus:border-brand-500 focus:outline-none">
            {["Google", "Podium", "Facebook", "BBB", "Other"].map((s) => <option key={s}>{s}</option>)}
          </select>
          <select value={stars} onChange={(e) => setStars(Number(e.target.value))} className="rounded-md border border-hairline px-2 py-2 text-sm focus:border-brand-500 focus:outline-none">
            {[5, 4, 3].map((n) => <option key={n} value={n}>{n} stars</option>)}
          </select>
          <textarea value={quote} onChange={(e) => setQuote(e.target.value)} rows={2} placeholder="Best line from the review (optional)"
            className="col-span-2 rounded-md border border-hairline px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100">Cancel</button>
          <button type="button" disabled={!empno || add.isPending}
            onClick={() => add.mutate({ empno, reviewer_name: reviewer || undefined, source, stars, quote: quote || undefined }, { onSuccess: onClose })}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-800 disabled:opacity-50">
            {add.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
          </button>
        </div>
      </div>
    </div>
  );
}

// ── the break-room TV ───────────────────────────────────────────────────────
const SLIDE_MS = 12_000;

export function TvScoreboard() {
  const { data: week } = useScoreboard("week");
  const { data: month } = useScoreboard("month");
  const { data: techs } = useAllActiveTechs();
  const [slide, setSlide] = useState(0);
  const [clock, setClock] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => s + 1), SLIDE_MS);
    const c = setInterval(() => setClock(new Date()), 30_000);
    return () => { clearInterval(t); clearInterval(c); };
  }, []);

  const nameOf = useMemo(() => new Map((techs ?? []).map((t) => [t.empno, (t.firstname ?? "") || (t.empname ?? "") || t.empno])), [techs]);
  const weekTop = (week?.rows ?? []).filter((r) => r.calls > 0).slice(0, 8);
  const planTop = ([...(month?.rows ?? [])].sort((a, b) => b.plans - a.plans)).filter((r) => r.plans > 0).slice(0, 8);
  const shouts = (month?.shoutoutList ?? []).slice(0, 4);

  const slides: { key: string; render: () => React.ReactNode }[] = [
    {
      key: "week",
      render: () => (
        <TvFrame title="This week" sub={`calls completed · since ${fmtDay(week?.start ?? null)}`}>
          <ul className="divide-y divide-slate-800/70">
            {weekTop.map((r, i) => <LeaderRow key={r.empno} r={r} rank={i + 1} maxCalls={Math.max(1, ...weekTop.map((x) => x.calls))} dark />)}
            {weekTop.length === 0 && <li className="py-10 text-center text-2xl text-slate-500">Quiet so far this week</li>}
          </ul>
        </TvFrame>
      ),
    },
    {
      key: "kpis",
      render: () => (
        <TvFrame title="The month so far" sub={`since ${fmtDay(month?.start ?? null)}`}>
          <div className="grid flex-1 grid-cols-2 content-center gap-6">
            <TvKpi label="Calls Completed" value={String(month?.totals.calls ?? "…")} cls="text-brand-400" />
            <TvKpi label="PLUS Plans Sold" value={String(month?.totals.plans ?? "…")} cls="text-emerald-400" />
            <TvKpi label="Review Shout-Outs" value={String(month?.totals.shoutouts ?? "…")} cls="text-amber-400" />
            <TvKpi label={`Invoiced${month?.invoicedAsOf ? ` (thru ${fmtDay(month.invoicedAsOf)})` : ""}`} value={month ? money0(month.totals.invoiced) : "…"} cls="text-slate-200" />
          </div>
        </TvFrame>
      ),
    },
    {
      key: "plans",
      render: () => (
        <TvFrame title="PLUS plans sold" sub="this month — memberships are the long game">
          <ul className="divide-y divide-slate-800/70">
            {planTop.map((r, i) => (
              <li key={r.empno} className="flex items-center gap-4 px-4 py-3.5">
                <span className={cn("flex h-9 w-9 items-center justify-center rounded-full text-lg font-bold tabular-nums", i === 0 ? "bg-emerald-900/60 text-emerald-300" : "bg-slate-800 text-slate-400")}>{i + 1}</span>
                <span className="flex-1 truncate text-xl font-semibold text-slate-100">{r.name}</span>
                <span className="text-2xl font-bold tabular-nums text-emerald-400">{r.plans}</span>
              </li>
            ))}
            {planTop.length === 0 && <li className="py-10 text-center text-2xl text-slate-500">No plans sold yet this month</li>}
          </ul>
        </TvFrame>
      ),
    },
    {
      key: "shouts",
      render: () => (
        <TvFrame title="Customers said it best" sub="recent review shout-outs">
          <div className="flex flex-1 flex-col justify-center gap-5 px-2">
            {shouts.map((s) => (
              <div key={s.id} className="rounded-2xl bg-slate-900/80 p-5 ring-1 ring-slate-800">
                <p className="text-xl leading-snug text-slate-200">&ldquo;{s.quote ?? "Great service!"}&rdquo;</p>
                <p className="mt-2 text-sm text-slate-400">
                  <span className="text-amber-400">{"★".repeat(Math.max(1, Math.min(5, s.stars ?? 5)))}</span>
                  {" "}for <span className="font-semibold text-slate-200">{s.empno ? (nameOf.get(s.empno) ?? s.empno) : "the team"}</span>
                  {s.reviewer_name ? ` — ${s.reviewer_name}` : ""}{s.source ? ` · ${s.source}` : ""}
                </p>
              </div>
            ))}
            {shouts.length === 0 && <p className="text-center text-2xl text-slate-500">Log the next 5-star review and it shows up here</p>}
          </div>
        </TvFrame>
      ),
    },
  ];
  const active = ((slide % slides.length) + slides.length) % slides.length;

  return (
    <div className="flex h-screen w-screen flex-col bg-slate-950 text-slate-100">
      <header className="flex items-center gap-3 px-8 pb-2 pt-6">
        <span className="text-lg font-bold tracking-tight text-slate-100">Sedgwick <span className="text-brand-400">Scoreboard</span></span>
        <span className="ml-auto text-sm tabular-nums text-slate-400">
          {clock.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} · {clock.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
        </span>
        <button type="button" onClick={() => document.documentElement.requestFullscreen?.()}
          title="Fullscreen (then cast the tab from Chrome ⋮ → Cast, or AirPlay the window)"
          className="rounded p-1.5 text-slate-500 transition-colors hover:bg-slate-800 hover:text-slate-200">
          <Expand className="h-4 w-4" />
        </button>
      </header>
      <main className="min-h-0 flex-1 px-8 pb-4">
        <div key={slides[active].key} className="flex h-full flex-col animate-[fadein_600ms_ease-out]">
          {slides[active].render()}
        </div>
      </main>
      <footer className="flex items-center justify-center gap-2 pb-5">
        {slides.map((s, i) => (
          <button key={s.key} type="button" onClick={() => setSlide(i)}
            className={cn("h-1.5 rounded-full transition-all", i === active ? "w-8 bg-brand-500" : "w-3 bg-slate-700 hover:bg-slate-600")} />
        ))}
      </footer>
      <style>{`@keyframes fadein { from { opacity: 0; transform: translateY(6px);} to { opacity: 1; transform: none;} }`}</style>
    </div>
  );
}

function TvFrame({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <section className="flex h-full flex-col overflow-hidden rounded-3xl bg-slate-900/50 ring-1 ring-slate-800">
      <div className="px-6 pb-3 pt-5">
        <h2 className="text-3xl font-bold tracking-tight text-slate-100">{title}</h2>
        {sub && <p className="mt-0.5 text-sm text-slate-400">{sub}</p>}
      </div>
      <div className="min-h-0 flex-1 overflow-hidden px-2 pb-4">{children}</div>
    </section>
  );
}

function TvKpi({ label, value, cls }: { label: string; value: string; cls: string }) {
  return (
    <div className="rounded-2xl bg-slate-900/80 p-8 text-center ring-1 ring-slate-800">
      <div className={cn("text-6xl font-bold tabular-nums tracking-tight", cls)}>{value}</div>
      <div className="mt-2 text-base text-slate-400">{label}</div>
    </div>
  );
}

// ── TV-SLIDES-01: Service Productivity (the % board from the TV deck) ──────
function ServiceProductivityBoard() {
  const { data } = useServiceProductivity();
  const rows = data ?? [];
  if (rows.length === 0) return null;
  const weekOf = rows[0]?.weekLabel;
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-2">
        <Wrench className="h-4 w-4 text-brand-500" />
        <h3 className="text-[13px] font-semibold text-slate-700">Service Productivity</h3>
        <InfoTip label="How the % is figured">% = (ticket $ ÷ hours) ÷ RHP, the same math as the payroll week. Techs without a pay basis show hours and $ only.</InfoTip>
        <span className="ml-auto text-[11px] text-slate-400">last full week{weekOf ? ` · ${weekOf}` : ""} · hours · ticket $ · %</span>
      </div>
      <ul className="grid gap-px bg-slate-100 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((r) => (
          <li key={r.empno} className="flex items-center gap-3 bg-white px-4 py-3">
            <TechAvatar empno={r.empno} name={r.name} size="md" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-slate-800">{r.name}</span>
              <span className="block text-[11px] tabular-nums text-slate-400">{r.hours.toFixed(2)} hrs · {r.dollars.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })}</span>
            </span>
            <span className={cn(
              "shrink-0 text-xl font-extrabold tabular-nums tracking-tight",
              r.pct == null ? "text-slate-300" : r.pct >= 80 ? "text-emerald-600" : r.pct >= 50 ? "text-slate-700" : "text-amber-600",
            )}>
              {r.pct != null ? `${r.pct.toFixed(1)}%` : "—"}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ── TV-SLIDES-01: Install Sales race (count · avg ticket · margin vs 45%) ──
function InstallLeadersBoard() {
  const { data } = useInstallLeaders();
  const rows = data ?? [];
  if (rows.length === 0) return null;
  const year = new Date().getFullYear();
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-2">
        <DollarSign className="h-4 w-4 text-emerald-500" />
        <h3 className="text-[13px] font-semibold text-slate-700">Install Sales · {year}</h3>
        <InfoTip label="Where these numbers come from">Installed-department invoices this year, grouped by ESC salesman. Margin = (billed − labor − material − other) ÷ billed.</InfoTip>
        <span className="ml-auto text-[11px] text-slate-400">sales · avg ticket · margin (goal 45%+)</span>
      </div>
      <ul className="divide-y divide-slate-100">
        {rows.map((r, i) => (
          <li key={r.salesman} className="flex items-center gap-3 px-4 py-2.5">
            <span className={cn(
              "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold tabular-nums",
              i === 0 ? "bg-amber-100 text-amber-800" : i === 1 ? "bg-slate-200 text-slate-600" : i === 2 ? "bg-orange-100 text-orange-800" : "bg-slate-50 text-slate-400",
            )}>{i + 1}</span>
            {r.empno ? <TechAvatar empno={r.empno} name={r.name} size="sm" /> : null}
            <span className="w-36 min-w-0 shrink-0 truncate text-sm font-semibold text-slate-800">{r.name}</span>
            <span className="shrink-0 text-sm tabular-nums text-slate-700">{r.count} sold</span>
            <span className="min-w-0 flex-1" />
            <span className="shrink-0 text-sm tabular-nums text-slate-600">avg {r.avg.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })}</span>
            <span className={cn(
              "shrink-0 rounded-full px-2 py-0.5 text-xs font-bold tabular-nums",
              r.marginPct == null ? "bg-slate-50 text-slate-400" : r.marginPct >= 45 ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700",
            )}>
              {r.marginPct != null ? `${r.marginPct.toFixed(1)}%` : "—"}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
